import { useCustom, type HttpError } from "@refinedev/core";
import type { WorkflowProcessInfo } from "../types";

interface UseWorkflowOptions {
  itemType: string;
  itemId: string;
  enabled?: boolean;
}

interface UseWorkflowResult {
  workflow: WorkflowProcessInfo | null;
  isLoading: boolean;
  isError: boolean;
  error: HttpError | null;
  refetch: () => void;
}

/**
 * Hook to fetch workflow process information for an item.
 * Uses the generic items endpoint which handles all item types.
 *
 * @param itemType - The exact Aras ItemType name (e.g., "Lead", "Opportunity")
 * @param itemId - The item's ID
 * @param enabled - Whether to enable the fetch
 */
export const useWorkflow = ({
  itemType,
  itemId,
  enabled = true,
}: UseWorkflowOptions): UseWorkflowResult => {
  const shouldFetch = enabled && !!itemType && !!itemId;

  const {
    data: workflowData,
    isLoading,
    isError,
    error,
    refetch,
  } = useCustom<WorkflowProcessInfo>({
    // Use exact itemType name - no conversion
    url: `/items/${itemType}/${itemId}/workflow`,
    method: "get",
    queryOptions: {
      enabled: shouldFetch,
      staleTime: 60 * 1000, // 1 minute cache
      retry: 1, // Only retry once
    },
  });

  return {
    workflow: workflowData?.data || null,
    isLoading: shouldFetch && isLoading,
    isError,
    error: error || null,
    refetch,
  };
};
